import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Calendar,
  Edit2,
  Trash2,
  X,
  Clock,
  Plus,
  Loader2,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { axiosGateway } from '@/api/axios';
import { AVAILABILITY_PATHS } from '@/api/endpoints';
import useDisponibility from '../../hooks/useDisponibility';
import './availabilityPeriodsList.css';

const DURATION_OPTIONS = [15, 20, 30, 45, 60];

const makeSlot = (start = '09:00', end = '17:00') => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  startTime: start,
  endTime: end,
  _isoDate: null,
  backendSlotIds: [],
});

const DEFAULT_WEEKLY_SCHEDULE = [
  { dayOfWeek: 0, available: false, slots: [makeSlot()] },
  { dayOfWeek: 1, available: true, slots: [makeSlot()] },
  { dayOfWeek: 2, available: true, slots: [makeSlot()] },
  { dayOfWeek: 3, available: true, slots: [makeSlot()] },
  { dayOfWeek: 4, available: true, slots: [makeSlot()] },
  { dayOfWeek: 5, available: true, slots: [makeSlot()] },
  { dayOfWeek: 6, available: false, slots: [makeSlot()] },
];

function parseLocalDate(isoString) {
  if (!isoString) return null;

  const s = String(isoString)
    .replace(/^([^T]+) ([^T]+)$/, '$1T$2')
    .replace(/Z$/, '')
    .replace(/[+-]\d{2}:\d{2}$/, '');

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  const [datePart, timePart = '00:00:00'] = s.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min, sec = 0] = timePart.split(':').map(Number);

  return new Date(y, m - 1, d, h, min, Math.floor(sec));
}

function extractIsoDate(isoString) {
  if (!isoString) return null;
  return String(isoString).substring(0, 10);
}

function extractTime(isoString) {
  if (!isoString) return '09:00';

  const s = String(isoString);
  if (s.includes('T')) return s.substring(11, 16);

  return s.substring(0, 5);
}

function normalizeSchedule(schedule = []) {
  return schedule.map((day) => {
    const slots =
      Array.isArray(day.slots) && day.slots.length > 0
        ? day.slots.map((slot) => ({
            id:
              slot.id ||
              `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            startTime: slot.startTime || '09:00',
            endTime: slot.endTime || '17:00',
            _isoDate: slot._isoDate || null,
            backendSlotIds: Array.isArray(slot.backendSlotIds)
              ? slot.backendSlotIds
              : [],
          }))
        : [makeSlot(day.startTime || '09:00', day.endTime || '17:00')];

    return {
      dayOfWeek: day.dayOfWeek,
      available: !!day.available,
      slots,
    };
  });
}

function normalizeSlotsPayload(payload) {
  if (Array.isArray(payload?.slots)) return payload.slots;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data?.slots)) return payload.data.slots;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function timeToMinutes(time) {
  if (!time) return null;

  const [hours, minutes] = String(time).split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  return hours * 60 + minutes;
}

function rangesOverlap(startA, endA, startB, endB) {
  return startA < endB && endA > startB;
}

function getComparableDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return new Date(value);
  return parseLocalDate(value);
}

function getSlotDayOfWeek(slot) {
  if (slot?.dayOfWeek !== null && slot?.dayOfWeek !== undefined) {
    return Number(slot.dayOfWeek);
  }

  const date = getComparableDateTime(slot?.startTime);
  return date ? date.getDay() : null;
}

function findSelfOverlap(payload) {
  for (const day of payload.weeklySchedule || []) {
    if (!day?.available) continue;

    const ranges = Array.isArray(day.slots) ? day.slots : [];

    for (let i = 0; i < ranges.length; i += 1) {
      const firstStart = timeToMinutes(ranges[i]?.startTime);
      const firstEnd = timeToMinutes(ranges[i]?.endTime);
      if (firstStart === null || firstEnd === null || firstStart >= firstEnd) continue;

      for (let j = i + 1; j < ranges.length; j += 1) {
        const secondStart = timeToMinutes(ranges[j]?.startTime);
        const secondEnd = timeToMinutes(ranges[j]?.endTime);
        if (secondStart === null || secondEnd === null || secondStart >= secondEnd) continue;

        if (rangesOverlap(firstStart, firstEnd, secondStart, secondEnd)) {
          return {
            kind: 'self',
            dayOfWeek: day.dayOfWeek,
            startTime: ranges[i].startTime,
            endTime: ranges[i].endTime,
            conflictingStartTime: ranges[j].startTime,
            conflictingEndTime: ranges[j].endTime,
          };
        }
      }
    }
  }

  return null;
}

function findExistingConflict(payload, existingSlots) {
  const periodStart = parseLocalDate(`${payload.periodStart}T00:00:00`);
  const periodEnd = parseLocalDate(`${payload.periodEnd}T23:59:59`);

  if (!periodStart || !periodEnd) return null;

  const normalizedExisting = (existingSlots || [])
    .map((slot) => {
      const start = getComparableDateTime(slot?.startTime);
      const end = getComparableDateTime(slot?.endTime);
      if (!start || !end) return null;

      return {
        id: slot?.id,
        start,
        end,
        dayOfWeek: getSlotDayOfWeek(slot),
      };
    })
    .filter(Boolean);

  for (const day of payload.weeklySchedule || []) {
    if (!day?.available) continue;

    const dayOfWeek = Number(day.dayOfWeek);
    if (Number.isNaN(dayOfWeek)) continue;

    const current = new Date(periodStart);
    while (current <= periodEnd) {
      if (current.getDay() !== dayOfWeek) {
        current.setDate(current.getDate() + 1);
        continue;
      }

      for (const timeRange of day.slots || []) {
        const startMinutes = timeToMinutes(timeRange?.startTime);
        const endMinutes = timeToMinutes(timeRange?.endTime);
        if (startMinutes === null || endMinutes === null || startMinutes >= endMinutes) continue;

        const proposedStart = new Date(
          current.getFullYear(),
          current.getMonth(),
          current.getDate(),
          Math.floor(startMinutes / 60),
          startMinutes % 60,
          0,
          0
        );
        const proposedEnd = new Date(
          current.getFullYear(),
          current.getMonth(),
          current.getDate(),
          Math.floor(endMinutes / 60),
          endMinutes % 60,
          0,
          0
        );

        const conflict = normalizedExisting.find((slot) => {
          if (slot.dayOfWeek !== dayOfWeek) return false;
          return rangesOverlap(
            proposedStart.getTime(),
            proposedEnd.getTime(),
            slot.start.getTime(),
            slot.end.getTime()
          );
        });

        if (conflict) {
          return {
            kind: 'existing',
            dayOfWeek,
            startTime: format(conflict.start, 'yyyy-MM-dd HH:mm'),
            endTime: format(conflict.end, 'yyyy-MM-dd HH:mm'),
            conflictingSlotId: conflict.id,
          };
        }
      }

      current.setDate(current.getDate() + 1);
    }
  }

  return null;
}

export function groupSlotsIntoPeriods(slots) {
  if (!slots || slots.length === 0) return [];

  const buckets = new Map();
  const legacySlotsByDate = new Map();

  const createPeriod = (key, startDate, endDate, slotDuration) => ({
    id: key,
    startDate: parseLocalDate(`${startDate}T00:00:00`),
    endDate: parseLocalDate(`${endDate}T00:00:00`),
    periodStart: startDate,
    periodEnd: endDate,
    slotDuration,
    daySignatures: {},
    weeklySchedule: Array.from({ length: 7 }, (_, dayOfWeek) => ({
      dayOfWeek,
      available: false,
      slots: [],
    })),
  });

  const addSlot = (period, slot, isoDate) => {
    const dayOfWeek =
      slot.dayOfWeek !== null && slot.dayOfWeek !== undefined
        ? Number(slot.dayOfWeek)
        : parseLocalDate(`${isoDate}T00:00:00`).getDay();
    const schedule = period.weeklySchedule.find((day) => day.dayOfWeek === dayOfWeek);
    if (!schedule) return;

    schedule.available = true;
    const startTime = extractTime(slot.startTime);
    const endTime = extractTime(slot.endTime);
    let existing = schedule.slots.find((item) => item.startTime === startTime && item.endTime === endTime);

    if (!existing) {
      existing = {
        id: `${dayOfWeek}-${startTime}-${endTime}`,
        startTime,
        endTime,
        _isoDate: isoDate,
        backendSlotIds: [],
      };
      schedule.slots.push(existing);
    }

    if (slot.id !== null && slot.id !== undefined) {
      const realId = String(slot.id);
      if (!existing.backendSlotIds.includes(realId)) existing.backendSlotIds.push(realId);
    }
  };

  slots.forEach((slot) => {
    const explicitStart = extractIsoDate(slot.periodStart || slot.period_start);
    const explicitEnd = extractIsoDate(slot.periodEnd || slot.period_end);
    const slotDuration = Number(slot.slotDuration || slot.slot_duration || 30);

    if (explicitStart && explicitEnd && explicitStart < explicitEnd) {
      const key = `period-${explicitStart}|${explicitEnd}|${slotDuration}`;
      if (!buckets.has(key)) buckets.set(key, createPeriod(key, explicitStart, explicitEnd, slotDuration));
      addSlot(buckets.get(key), slot, extractIsoDate(slot.startTime));
      return;
    }

    const isoDate = extractIsoDate(slot.startTime);
    if (!isoDate) return;
    if (!legacySlotsByDate.has(isoDate)) legacySlotsByDate.set(isoDate, []);
    legacySlotsByDate.get(isoDate).push(slot);
  });

  // Older records may have one row per day without shared period bounds.
  // Coalesce nearby rows when their weekly schedule remains consistent.
  const legacyDays = [...legacySlotsByDate.entries()].sort(([left], [right]) => left.localeCompare(right));
  let currentLegacyPeriod = null;

  legacyDays.forEach(([isoDate, daySlots]) => {
    const date = parseLocalDate(`${isoDate}T00:00:00`);
    const dayOfWeek = getSlotDayOfWeek(daySlots[0]);
    const slotDuration = Number(daySlots[0]?.slotDuration || daySlots[0]?.slot_duration || 30);
    const daySignature = daySlots
      .map((slot) => `${extractTime(slot.startTime)}-${extractTime(slot.endTime)}-${Number(slot.slotDuration || slot.slot_duration || 30)}`)
      .sort()
      .join("|");
    const previousDate = currentLegacyPeriod?.endDate;
    const gapDays = previousDate
      ? Math.round((date.getTime() - previousDate.getTime()) / 86_400_000)
      : Infinity;
    const previousDaySignature = currentLegacyPeriod?.daySignatures[dayOfWeek];
    const canJoinPeriod = currentLegacyPeriod &&
      gapDays > 0 &&
      gapDays <= 7 &&
      currentLegacyPeriod.slotDuration === slotDuration &&
      (!previousDaySignature || previousDaySignature === daySignature);

    if (!canJoinPeriod) {
      const key = `daily-period-${isoDate}-${slotDuration}-${buckets.size}`;
      currentLegacyPeriod = createPeriod(key, isoDate, isoDate, slotDuration);
      currentLegacyPeriod.daySignatures[dayOfWeek] = daySignature;
      buckets.set(key, currentLegacyPeriod);
    } else {
      currentLegacyPeriod.endDate = date;
      currentLegacyPeriod.periodEnd = isoDate;
      currentLegacyPeriod.daySignatures[dayOfWeek] = daySignature;
    }

    daySlots.forEach((slot) => addSlot(currentLegacyPeriod, slot, isoDate));
  });

  return [...buckets.values()].map((period) => ({
    ...period,
    weeklySchedule: period.weeklySchedule.map((day) => ({
      ...day,
      slots: [...day.slots].sort((a, b) =>
        a.startTime.localeCompare(b.startTime)
      ),
    })),
  })).sort((left, right) => left.startDate - right.startDate);
}

async function fetchAllSlotsFromExistingRoute(medecinId) {
  try {
    const response = await axiosGateway.get(
      AVAILABILITY_PATHS.GET_ALL_SLOTS(medecinId)
    );

    const slots = normalizeSlotsPayload(response?.data);
    if (slots.length > 0) return slots;
  } catch (_) {}

  const today = new Date();
  const dates = [];

  for (let offset = -30; offset <= 90; offset++) {
    const d = new Date(today);
    d.setDate(today.getDate() + offset);
    dates.push(format(d, 'yyyy-MM-dd'));
  }

  const BATCH_SIZE = 10;
  const allSlots = [];
  const seen = new Set();

  for (let i = 0; i < dates.length; i += BATCH_SIZE) {
    const batch = dates.slice(i, i + BATCH_SIZE);

    const results = await Promise.allSettled(
      batch.map((date) =>
        axiosGateway.get(AVAILABILITY_PATHS.GET_SLOTS_FOR_DAY(medecinId, date))
      )
    );

    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;

      const slots = normalizeSlotsPayload(result.value?.data);

      slots.forEach((slot) => {
        const key = String(slot.id);

        if (!seen.has(key)) {
          seen.add(key);
          allSlots.push(slot);
        }
      });
    });
  }

  return allSlots;
}

function useScrollLock(active) {
  useEffect(() => {
    if (!active) return;

    const scrollY = window.scrollY;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflowY = 'scroll';

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflowY = '';
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

function DeleteConfirmModal({ period, onConfirm, onCancel, loading, t }) {
  return (
    <div className="apl-confirm-overlay" onClick={onCancel}>
      <div className="apl-confirm" onClick={(e) => e.stopPropagation()}>
        <div className="apl-confirm__icon">
          <AlertTriangle size={28} />
        </div>

        <h3 className="apl-confirm__title">
          {t('periods.delete.title')}
        </h3>

        <p
          className="apl-confirm__desc"
          dangerouslySetInnerHTML={{
            __html: t('periods.delete.description', {
              startDate: format(period.startDate, 'dd/MM/yyyy'),
              endDate: format(period.endDate, 'dd/MM/yyyy'),
            }),
          }}
        />

        <div className="apl-confirm__actions">
          <button
            type="button"
            className="apl-confirm__btn apl-confirm__btn--cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {t('periods.delete.cancel')}
          </button>

          <button
            type="button"
            className="apl-confirm__btn apl-confirm__btn--delete"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={14} className="apl-spin" />
            ) : (
              <Trash2 size={14} />
            )}
            {t('periods.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact card summarising a single period — matches the
 * "Availability Periods" design: date range, meta line, and a
 * Start / End preview grid pulled from the first available day.
 */
function PeriodCard({ period, onEdit, onDelete, t, currentLang }) {
  const availableCount = period.weeklySchedule.filter((s) => s.available).length;

  const firstAvailableDay =
    period.weeklySchedule.find((s) => s.available && s.slots?.length) || null;

  const previewSlots = (firstAvailableDay?.slots || []).slice(0, 2);

  return (
    <div className="apl-item">
      <div className="apl-item__actions">
        <button
          className="apl-btn apl-btn--edit"
          onClick={onEdit}
          type="button"
          title={t('periods.actions.edit')}
        >
          <Edit2 size={14} />
        </button>

        <button
          className="apl-btn apl-btn--delete"
          onClick={onDelete}
          type="button"
          title={t('periods.actions.delete')}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="apl-item__dates">
        <Calendar className="apl-item__icon" size={15} />
        <span className="apl-item__range">
          {new Intl.DateTimeFormat(currentLang || 'en', { month: 'short', day: 'numeric', year: 'numeric' }).format(period.startDate)} – {new Intl.DateTimeFormat(currentLang || 'en', { month: 'short', day: 'numeric', year: 'numeric' }).format(period.endDate)}
        </span>
      </div>

      <p className="apl-item__meta">
        {t('periods.availableDays', { count: availableCount })} ·{' '}
        {t('periods.slotDuration', { duration: period.slotDuration ?? 30 })}
      </p>

      {previewSlots.length > 0 && (
        <div className="apl-item__preview">
          {previewSlots.map((slot) => (
            <div key={slot.id} className="apl-item__preview-row">
              <span className="apl-item__preview-label">{t('periods.form.startTime', { defaultValue: 'Start :' })}</span>
              <span className="apl-item__preview-value">{slot.startTime}</span>
              <ArrowRight size={12} className="apl-item__preview-arrow" />
              <span className="apl-item__preview-label">{t('periods.form.endTimeLabel', { defaultValue: 'End :' })}</span>
              <span className="apl-item__preview-value">{slot.endTime}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AvailabilityPeriodsList({ medecinId, onSlotsLoaded }) {
  const { t, i18n } = useTranslation('availability');

  const [modalMode, setModalMode] = useState(null);
  const [editingPeriodId, setEditingPeriodId] = useState(null);

  const [rawSlots, setRawSlots] = useState([]);
  const [allLoadedSlots, setAllLoadedSlots] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [periodsError, setPeriodsError] = useState(null);

  const [deletingPeriod, setDeletingPeriod] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [formSlotDuration, setFormSlotDuration] = useState(30);
  const [customDuration, setCustomDuration] = useState(90);
  const [formWeeklySchedule, setFormWeeklySchedule] = useState(
    normalizeSchedule(DEFAULT_WEEKLY_SCHEDULE)
  );

  const {
    createFromPeriod,
    deleteSlot,
    loadingCreate,
    firstError,
    clearAllErrors,
  } = useDisponibility({ medecinId });

  const periods = useMemo(() => groupSlotsIntoPeriods(rawSlots), [rawSlots]);

  const periodsCountLabel = t('periods.count', {
    count: periods.length,
  });

  const hasScrollableList = periods.length > 2;

  useScrollLock(!!modalMode);

  const loadPeriods = useCallback(async () => {
    if (!medecinId) return;

    setLoadingPeriods(true);
    setPeriodsError(null);

    try {
      const slots = await fetchAllSlotsFromExistingRoute(medecinId);
      const availableSlots = slots.filter((slot) => slot.isAvailable !== false);

      setAllLoadedSlots(slots);
      setRawSlots(availableSlots);
      onSlotsLoaded?.(availableSlots);
    } catch (error) {
      setPeriodsError(
        error?.response?.data?.message ||
          error?.message ||
          t('errors.loadError')
      );
    } finally {
      setLoadingPeriods(false);
    }
  }, [medecinId, onSlotsLoaded, t]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const resetForm = () => {
    setFormStartDate('');
    setFormEndDate('');
    setFormSlotDuration(30);
    setCustomDuration(90);
    setFormWeeklySchedule(normalizeSchedule(DEFAULT_WEEKLY_SCHEDULE));
    setEditingPeriodId(null);
  };

  const openAddModal = () => {
    resetForm();
    // Auto-populate start date with next available date
    const nextAvailable = findNextAvailableStartDate();
    setFormStartDate(nextAvailable);
    // Set end date to be at least 1 week after start
    const endDate = new Date(parseLocalDate(`${nextAvailable}T00:00:00`));
    endDate.setDate(endDate.getDate() + 7);
    setFormEndDate(format(endDate, 'yyyy-MM-dd'));
    setModalMode('add');
  };

  const closeModal = () => {
    setModalMode(null);
    resetForm();
  };

  const openEditModal = (period) => {
    setEditingPeriodId(period.id);
    setFormStartDate(format(period.startDate, 'yyyy-MM-dd'));
    setFormEndDate(format(period.endDate, 'yyyy-MM-dd'));
    setFormSlotDuration(period.slotDuration ?? 30);

    const predefined = DURATION_OPTIONS;

    setCustomDuration(
      predefined.includes(period.slotDuration) ? 90 : period.slotDuration ?? 90
    );

    setFormWeeklySchedule(
      normalizeSchedule(period.weeklySchedule || DEFAULT_WEEKLY_SCHEDULE)
    );

    setModalMode('edit');
  };

  const updateDayAvailability = (dayOfWeek, available) =>
    setFormWeeklySchedule((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek ? { ...day, available } : day
      )
    );

  const updateSlotField = (dayOfWeek, slotId, field, value) =>
    setFormWeeklySchedule((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              slots: day.slots.map((slot) =>
                slot.id === slotId ? { ...slot, [field]: value } : slot
              ),
            }
          : day
      )
    );

  const addSlotToDay = (dayOfWeek) =>
    setFormWeeklySchedule((prev) =>
      prev.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              available: true,
              slots: [...day.slots, makeSlot()],
            }
          : day
      )
    );

  const removeSlotFromDay = (dayOfWeek, slotId) =>
    setFormWeeklySchedule((prev) =>
      prev.map((day) => {
        if (day.dayOfWeek !== dayOfWeek) return day;

        const next = day.slots.filter((slot) => slot.id !== slotId);

        return {
          ...day,
          slots: next.length > 0 ? next : [makeSlot()],
        };
      })
    );

  const buildCreatePayload = () => ({
    id: Date.now().toString(),
    startDate: parseLocalDate(`${formStartDate}T00:00:00`),
    endDate: parseLocalDate(`${formEndDate}T00:00:00`),
    slotDuration: Number(formSlotDuration),
    periodStart: formStartDate,
    periodEnd: formEndDate,
    weeklySchedule: formWeeklySchedule.map((day) => ({
      dayOfWeek: day.dayOfWeek,
      available: day.available,
      slots: (day.slots || []).map((slot) => ({
        startTime: slot.startTime,
        endTime: slot.endTime,
      })),
    })),
  });

  const getValidationSlots = useCallback(
    (excludeIds = []) => {
      const excluded = new Set(excludeIds.map((id) => String(id)));
      return allLoadedSlots.filter((slot) => !excluded.has(String(slot.id)));
    },
    [allLoadedSlots]
  );

  const formatOverlapMessage = useCallback(
    (conflict) => {
      if (conflict?.kind === 'self') {
        return t('errors.overlapDetected', {
          defaultValue:
            `Deux plages du même formulaire se chevauchent le jour ${conflict.dayOfWeek}. Corrigez vos horaires avant d'enregistrer.`,
        });
      }

      return t('errors.overlapDetected', {
        defaultValue:
          `Conflit : une plage existe déjà (jour ${conflict?.dayOfWeek}, ${conflict?.startTime} → ${conflict?.endTime}). Modifiez vos horaires.`,
        dayOfWeek: conflict?.dayOfWeek,
        startTime: conflict?.startTime,
        endTime: conflict?.endTime,
      });
    },
    [t]
  );

  const validatePayload = useCallback(
    (payload, slotsForValidation) => {
      const selfConflict = findSelfOverlap(payload);
      if (selfConflict) return selfConflict;

      const existingConflict = findExistingConflict(payload, slotsForValidation);
      if (existingConflict) return existingConflict;

      return null;
    },
    []
  );

  /**
   * Find the next untracked (available) start date
   * by scanning existing periods
   */
  const findNextAvailableStartDate = useCallback(() => {
    if (!rawSlots || rawSlots.length === 0) {
      // No existing periods, start from today
      return format(new Date(), 'yyyy-MM-dd');
    }

    // Group periods and find their end dates
    const existingPeriods = periods.map((p) => ({
      startDate: p.startDate,
      endDate: p.endDate,
    }));

    // Sort by end date
    existingPeriods.sort((a, b) => a.endDate.getTime() - b.endDate.getTime());

    // Get the latest end date
    const latestEndDate = existingPeriods[existingPeriods.length - 1]?.endDate;
    if (!latestEndDate) return format(new Date(), 'yyyy-MM-dd');

    // Add 1 day to get next available start
    const nextStart = new Date(latestEndDate);
    nextStart.setDate(nextStart.getDate() + 1);

    return format(nextStart, 'yyyy-MM-dd');
  }, [rawSlots, periods]);

  /**
   * Auto-trim conflicting existing period:
   * If new period starts within an existing period,
   * delete ALL slots from that conflicting period
   * to prevent any overlap
   */
  const autoTrimConflictingPeriod = useCallback(
    async (newStartDate) => {
      const newStart = parseLocalDate(`${newStartDate}T00:00:00`);
      if (!newStart) return;

      // Find any period that contains this start date
      const conflictingPeriod = periods.find(
        (p) => p.startDate <= newStart && p.endDate >= newStart
      );

      if (!conflictingPeriod) return; // No conflict, nothing to trim


      // Collect ALL slot IDs from the conflicting period
      const slotIdsToDelete = [
        ...new Set(
          conflictingPeriod.weeklySchedule
            .flatMap((day) => day.slots || [])
            .flatMap((slot) => slot.backendSlotIds || [])
            .filter((id) => id && !Number.isNaN(Number(id)))
        ),
      ];

      if (slotIdsToDelete.length === 0) {
        return;
      }


      // Delete all slots from the conflicting period
      for (const slotId of slotIdsToDelete) {
        try {
          await deleteSlot(slotId);
        } catch (err) {
          console.error(`[AUTO-TRIM] ✗ Failed to delete slot ${slotId}:`, err);
          // Continue deleting other slots even if one fails
        }
      }

      // Reload periods to ensure fresh state
      await loadPeriods();
    },
    [periods, deleteSlot, loadPeriods]
  );

  const handleEditByRecreate = async () => {
    const period = periods.find((p) => p.id === editingPeriodId);
    if (!period) return;

    setPeriodsError(null);

    const existingSlotIds = [
      ...new Set(
        period.weeklySchedule
          .flatMap((day) => day.slots || [])
          .flatMap((slot) => slot.backendSlotIds || [])
          .filter((id) => id && !Number.isNaN(Number(id)))
      ),
    ];

    if (existingSlotIds.length === 0) {
      setPeriodsError(t('errors.noBackendSlots'));
      return;
    }

    const payload = buildCreatePayload();
    const conflict = validatePayload(payload, getValidationSlots(existingSlotIds));

    if (conflict) {
      setPeriodsError(formatOverlapMessage(conflict));
      return;
    }

    for (const slotId of existingSlotIds) {
      const deleteResult = await deleteSlot(slotId);

      if (deleteResult?.error) {
        const msg =
          deleteResult?.payload ||
          deleteResult?.error?.message ||
          t('errors.deleteSlotErrorGeneric', { slotId });

        if (
          String(msg).toLowerCase().includes('token') ||
          String(msg).toLowerCase().includes('unauthorized') ||
          String(msg).includes('401')
        ) {
          setPeriodsError(t('errors.sessionExpired'));
        } else {
          setPeriodsError(t('errors.deleteSlotError', { slotId }));
        }

        await loadPeriods();
        return;
      }
    }

    await loadPeriods();

    try {
      await createFromPeriod(medecinId, payload);
      await loadPeriods();
      closeModal();
    } catch (err) {
      if (err.code === 'Overlap Detected' && err.conflictingSlot) {
        const { startTime, endTime, dayOfWeek } = err.conflictingSlot;
        setPeriodsError(
          t('errors.overlapDetected', {
            defaultValue: `Conflit : une plage existe déjà (jour ${dayOfWeek}, ${startTime} → ${endTime}). Modifiez vos horaires.`,
            dayOfWeek,
            startTime,
            endTime,
          })
        );
      } else {
        setPeriodsError(err.message || t('errors.createModifiedPeriodError'));
      }
      await loadPeriods();
    }
  };

const handleAdd = async () => {
  setPeriodsError(null);

  try {
    // STEP 1: Auto-trim any conflicting existing periods
    await autoTrimConflictingPeriod(formStartDate);

    // CRITICAL: Wait a moment for backend to process deletions
    await new Promise((resolve) => setTimeout(resolve, 500));

    // STEP 1.5: Reload to ensure fresh data after trimming
    await loadPeriods();

    // STEP 2: Build payload for the new period
    const payload = buildCreatePayload();

    // STEP 3: Validate (self-overlap check mainly, since we trimmed existing conflicts)
    const selfConflict = findSelfOverlap(payload);
    if (selfConflict) {
      setPeriodsError(formatOverlapMessage(selfConflict));
      return;
    }


    // STEP 4: Create the new period
    await createFromPeriod(medecinId, payload);

    await loadPeriods();
    closeModal();
  } catch (err) {
    console.error('[HANDLE-ADD] Error:', err);
    if (err.code === 'Overlap Detected' && err.conflictingSlot) {
      const { startTime, endTime, dayOfWeek } = err.conflictingSlot;
      setPeriodsError(
        t('errors.overlapDetected', {
          defaultValue: `Conflit : une plage existe déjà (jour ${dayOfWeek}, ${startTime} → ${endTime}). Modifiez vos horaires.`,
          dayOfWeek,
          startTime,
          endTime,
        })
      );
    } else {
      setPeriodsError(err.message || t('errors.createPeriodError'));
    }
  }
};

  const handleSubmit = async () => {
    if (!formStartDate || !formEndDate || !medecinId) return;

    if (modalMode === 'edit' && editingPeriodId) {
      await handleEditByRecreate();
      return;
    }

    await handleAdd();
  };

const askDeletePeriod = (period) => {
  setDeletingPeriod(period);
};
  const confirmDeletePeriod = useCallback(async () => {
    if (!deletingPeriod) return;

    const slotIds = [
      ...new Set(
        deletingPeriod.weeklySchedule
          .flatMap((day) => day.slots || [])
          .flatMap((slot) => slot.backendSlotIds || [])
          .filter((id) => id && !Number.isNaN(Number(id)))
      ),
    ];

    if (slotIds.length === 0) {
      setDeletingPeriod(null);
      return;
    }

    setLoadingDelete(true);

    try {
      for (const slotId of slotIds) {
        const deleteResult = await deleteSlot(slotId);

        if (deleteResult?.error) {
          const msg =
            deleteResult?.payload ||
            deleteResult?.error?.message ||
            t('errors.deleteSlotErrorGeneric', { slotId });

          if (
            String(msg).toLowerCase().includes('token') ||
            String(msg).toLowerCase().includes('unauthorized') ||
            String(msg).includes('401')
          ) {
            setPeriodsError(t('errors.sessionExpired'));
          } else {
            setPeriodsError(t('errors.deleteSlotErrorGeneric', { slotId }));
          }

          return;
        }
      }

      await loadPeriods();
    } finally {
      setLoadingDelete(false);
      setDeletingPeriod(null);
    }
  }, [deletingPeriod, deleteSlot, loadPeriods, t]);

  return (
    <>
      {deletingPeriod && (
        <DeleteConfirmModal
          period={deletingPeriod}
          loading={loadingDelete}
          onConfirm={confirmDeletePeriod}
          onCancel={() => setDeletingPeriod(null)}
          t={t}
        />
      )}


      <div className="apl-card">
        <div className="apl-card__header">
          <div className="apl-card__heading">
            <h3 className="apl-card__title">{t('periods.title')}</h3>
          </div>

          <span className="apl-card__badge">{periodsCountLabel}</span>
        </div>

        {loadingPeriods && periods.length === 0 ? (
          <div className="apl-empty">
            <Loader2 size={28} className="apl-spin" /> 
            <p className="apl-empty__title">{t('periods.loading')}</p>
          </div>
        ) : periods.length === 0 ? (
          <div className="apl-empty">
            <Calendar className="apl-empty__icon" />
            <p className="apl-empty__title">{t('periods.noPeriods')}</p>
            <p className="apl-empty__sub">{t('periods.noPeriodsHint')}</p>
          </div>
        ) : (
          <div
            className={`apl-list-wrap${
              hasScrollableList ? ' apl-list-wrap--scrollable' : ''
            }`}
          >
            <div className="apl-list">
              {periods.map((period) => (
                <PeriodCard
                  key={period.id}
                  period={period}
                  onEdit={() => openEditModal(period)}
                  onDelete={() => askDeletePeriod(period)}
                  t={t}
                  currentLang={i18n.language}
                />
              ))}
            </div>
          </div>
        )}

        <div className="apl-card__footer">
          <button
            type="button"
            className="apl-add-btn"
            onClick={openAddModal}
            disabled={loadingCreate || loadingPeriods}
          >
            {loadingCreate || loadingPeriods ? (
              <Loader2 size={16} className="apl-spin" />
            ) : (
              <Plus size={16} />
            )}
            <span>{t('periods.addPeriod')}</span>
          </button>
        </div>
      </div>

      {modalMode && (
        <div className="apl-modal-overlay" onClick={closeModal}>
          <div className="apl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="apl-modal__header">
              <div>
                <h2 className="apl-modal__title">
                  {modalMode === 'add'
                    ? t('periods.form.addTitle')
                    : t('periods.form.editTitle')}
                </h2>

                <p className="apl-modal__subtitle">
                  {modalMode === 'add'
                    ? t('periods.form.addSubtitle')
                    : t('periods.form.editSubtitle')}
                </p>
              </div>

              <button
                className="apl-modal__close"
                onClick={closeModal}
                type="button"
              >
                <X size={20} />
              </button>
            </div>

            <div className="apl-modal__body">
              <div className="apl-modal__form">
                <div className="apl-modal__section">
                  <label className="apl-modal__section-label">
                    {modalMode === 'add'
                      ? t('periods.form.periodDuration')
                      : t('periods.form.periodDurationEdit')}
                  </label>

                  <div className="apl-modal__date-grid">
                    <div className="apl-modal__field">
                      <label className="apl-modal__field-label">
                        {t('periods.form.startDate')}
                      </label>

                      <input
                        type="date"
                        value={formStartDate}
                        onChange={(e) => setFormStartDate(e.target.value)}
                        className="apl-modal__input"
                      />
                    </div>

                    <div className="apl-modal__field">
                      <label className="apl-modal__field-label">
                        {t('periods.form.endDate')}
                      </label>

                      <input
                        type="date"
                        value={formEndDate}
                        min={formStartDate}
                        onChange={(e) => setFormEndDate(e.target.value)}
                        className="apl-modal__input"
                      />
                    </div>
                  </div>
                </div>

                <div className="apl-modal__section">
                  <div className="apl-modal__duration-title">
                    <Clock size={16} />
                    <span>
                      {modalMode === 'add'
                        ? t('periods.form.consultationDuration')
                        : t('periods.form.slotDurationEdit')}
                    </span>
                  </div>

                  <div className="apl-modal__duration-grid">
                    {DURATION_OPTIONS.map((duration) => (
                      <button
                        key={duration}
                        type="button"
                        className={`apl-modal__duration-btn${
                          formSlotDuration === duration
                            ? ' apl-modal__duration-btn--active'
                            : ''
                        }`}
                        onClick={() => setFormSlotDuration(duration)}
                      >
                        {t(`periods.durations.${duration}`)}
                      </button>
                    ))}

                    <div
                      className={`apl-modal__duration-btn apl-modal__duration-btn--custom${
                        !DURATION_OPTIONS.includes(formSlotDuration)
                          ? ' apl-modal__duration-btn--active'
                          : ''
                      }`}
                    >
                      <input
                        type="number"
                        min="1"
                        value={customDuration}
                        onChange={(e) => {
                          const value = Number(e.target.value) || 0;
                          setCustomDuration(value);
                          setFormSlotDuration(value);
                        }}
                        className="apl-modal__duration-input"
                      />

                      <span className="apl-modal__duration-suffix">
                        {t('periods.form.customMin')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="apl-modal__section">
                  <label className="apl-modal__section-label">
                    {modalMode === 'add'
                      ? t('periods.form.weeklySchedule')
                      : t('periods.form.weeklyScheduleEdit')}
                  </label>

                  <div className="apl-editor">
                    {formWeeklySchedule.map((day) => (
                      <div
                        key={day.dayOfWeek}
                        className={`apl-editor__row${
                          day.available ? ' apl-editor__row--active' : ''
                        }`}
                      >
                        <div className="apl-editor__left">
                          <label className="apl-editor__switch">
                            <input
                              type="checkbox"
                              checked={day.available}
                              onChange={(e) =>
                                updateDayAvailability(
                                  day.dayOfWeek,
                                  e.target.checked
                                )
                              }
                            />
                            <span className="apl-editor__slider" />
                          </label>

                          <span className="apl-editor__day">
                            {t(`periods.days.${day.dayOfWeek}`)}
                          </span>
                        </div>

                        <div className="apl-editor__right">
                          {day.available ? (
                            <div className="apl-editor__slots">
                              {day.slots.map((slot) => (
                                <div
                                  key={slot.id}
                                  className="apl-editor__slot-row"
                                >
                                  <input
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(e) =>
                                      updateSlotField(
                                        day.dayOfWeek,
                                        slot.id,
                                        'startTime',
                                        e.target.value
                                      )
                                    }
                                    className="apl-editor__time-input"
                                  />

                                  <span className="apl-editor__sep">
                                    {t('periods.form.to')}
                                  </span>

                                  <input
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(e) =>
                                      updateSlotField(
                                        day.dayOfWeek,
                                        slot.id,
                                        'endTime',
                                        e.target.value
                                      )
                                    }
                                    className="apl-editor__time-input"
                                  />

                                  <button
                                    type="button"
                                    className="apl-editor__plus-btn"
                                    onClick={() => addSlotToDay(day.dayOfWeek)}
                                    title={t('periods.actions.addSlot')}
                                  >
                                    +
                                  </button>

                                  {day.slots.length > 1 && (
                                    <button
                                      type="button"
                                      className="apl-editor__remove-btn"
                                      onClick={() =>
                                        removeSlotFromDay(
                                          day.dayOfWeek,
                                          slot.id
                                        )
                                      }
                                      title={t('periods.actions.removeSlot')}
                                    >
                                      −
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="apl-editor__badge">
                              {t('periods.unavailable')}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="apl-modal__footer">
              <button
                type="button"
                className="apl-modal__footer-btn apl-modal__footer-btn--cancel"
                onClick={closeModal}
              >
                {t('periods.form.cancel')}
              </button>

              <button
                type="button"
                className="apl-modal__footer-btn apl-modal__footer-btn--confirm"
                onClick={handleSubmit}
                disabled={loadingCreate || loadingPeriods}
              >
                {loadingCreate || loadingPeriods ? (
                  <Loader2 size={14} className="apl-spin" />
                ) : modalMode === 'add' ? (
                  t('periods.form.add')
                ) : (
                  t('periods.form.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}